import React, { FC, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { CardSchema, ECardLayoutOptions, HighlightSchema } from 'constants/graphqlTypes';
import { COLORS } from 'styles/constants';
import { TEST_CARD_FULL_BLEED, getCardImageId, getCardSourceLabelId } from 'constants/aqa/card';

import handleVibrate from 'helpers/handleVibrate';
import getStyles from 'helpers/getStyles';
import getImages from 'helpers/getImages';
import Grain from 'components/UI/Grain';
import UIHighlight from 'components/UI/Highlight';
import { useReactionMenu } from 'components/Reactions/helpers/hooks';
import ReactionsModal from 'components/Reactions/ReactionsModal';
import Content from '../Common/Content';
import Source from '../Common/Source';
import Actions from '../Actions';
import QuestionAttribute from '../QuestionAttribute';

import { StyledImage, StyledLink, StyledWrap, StyledFooter, StyledContent, StyledDivider } from './styled';
import SparkAttribute from '../SparkAttribute';
import CardFooterMenu from '../../UI/CardFooterMenu/CardFooterMenu';

const FullBleed: FC<{ card: CardSchema; highlight?: HighlightSchema | null; dataTestId?: string }> = ({
  card,
  highlight,
  dataTestId,
}) => {
  const color = COLORS.white[100];

  const { cardStyle, externalSources, mediaTags, responseToQuestion, responseToSpark } = card;
  const { id: questionId, title: questionTitle, questionGuestList } = responseToQuestion ?? {};
  const { bgColor, layout } = getStyles(cardStyle);
  const { imageUrl } = getImages(cardStyle);
  const { imageUrl: imageGuestUrl } = getImages(questionGuestList?.cardStyle);
  const { push } = useHistory();
  const { items } = externalSources ?? {};
  const { sourceUrl } = items?.[0] ?? {};
  const { curator, introductionText, isShort } = highlight ?? {};

  const isVideo = mediaTags?.items[0]?.name === 'video' && layout !== ECardLayoutOptions.Plain;
  const showImage = !!(imageUrl && layout !== ECardLayoutOptions.Plain);

  const isPlainCard = layout === ECardLayoutOptions.Plain;

  // Animation state
  const [isAnimating, setIsAnimating] = useState(false);
  const [imageAnimationProgress, setImageAnimationProgress] = useState(0);
  const imageRef = useRef<HTMLDivElement>(null);
  const triggerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInTriggerZone = useRef(false);
  const lastScrollY = useRef(0);

  const {
    setIsReactionsModalOpen,
    setIsQuickReactionOpen,
    setReactionCoordinates,
    isReactionsModalOpen,
    isQuickReactionOpen,
  } = useReactionMenu();

  // iOS-style keyframe interpolation function
  const interpolateFromKeyframes = (progress: number, keyframes: number[]): number => {
    const kfProgress = [0.0, 0.25, 0.5, 0.6, 0.75, 1.0];

    // Find the two keyframes to interpolate between
    let lowerIndex = 0;
    let upperIndex = 1;

    for (let i = 0; i < kfProgress.length - 1; i += 1) {
      if (progress >= kfProgress[i] && progress <= kfProgress[i + 1]) {
        lowerIndex = i;
        upperIndex = i + 1;
        break;
      }
    }

    // If progress is beyond our keyframes, clamp to the last value
    if (progress > kfProgress[kfProgress.length - 1]) {
      return keyframes[keyframes.length - 1];
    }

    // Linear interpolation between the two keyframes
    const lowerProgress = kfProgress[lowerIndex];
    const upperProgress = kfProgress[upperIndex];
    const lowerValue = keyframes[lowerIndex];
    const upperValue = keyframes[upperIndex];

    const t = (progress - lowerProgress) / (upperProgress - lowerProgress);
    return lowerValue + (upperValue - lowerValue) * t;
  };

  // Breathing animation trigger logic
  useEffect(() => {
    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      const entry = entries[0];
      const { intersectionRatio, boundingClientRect } = entry;
      const viewportHeight = window.innerHeight;

      // Calculate trigger zone (center 80% of screen height)
      const triggerZoneTop = viewportHeight * 0.1; // 10% from top
      const triggerZoneBottom = viewportHeight * 0.9; // 90% from top

      // Check if card is in trigger zone and 70% visible
      const isInZone =
        boundingClientRect.top <= triggerZoneBottom &&
        boundingClientRect.bottom >= triggerZoneTop &&
        intersectionRatio >= 0.7;

      if (isInZone && !isInTriggerZone.current) {
        // Card entered trigger zone with 70% visibility
        isInTriggerZone.current = true;

        // Start 1-second timer before animation begins
        triggerTimeoutRef.current = setTimeout(() => {
          setIsAnimating(true);
        }, 1000);
      } else if (!isInZone && isInTriggerZone.current) {
        // Card left trigger zone
        isInTriggerZone.current = false;
        setIsAnimating(false);

        // Clear timeout if card leaves before 1 second
        if (triggerTimeoutRef.current) {
          clearTimeout(triggerTimeoutRef.current);
          triggerTimeoutRef.current = null;
        }
      }
    };

    // Intersection observer for trigger zone - observe the entire card, not just the image
    const imageObserver = new IntersectionObserver(handleIntersection, {
      threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
      rootMargin: '10% 0px -10% 0px', // Trigger zone: center 80% of screen
    });

    if (imageRef.current) {
      imageObserver.observe(imageRef.current);
    }

    return () => {
      imageObserver.disconnect();
      if (triggerTimeoutRef.current) {
        clearTimeout(triggerTimeoutRef.current);
      }
    };
  }, []);

  // Scroll-tethered image animation logic (original)
  useEffect(() => {
    const handleScroll = () => {
      if (!imageRef.current || !isInTriggerZone.current) return;

      const currentScrollY = window.scrollY;
      lastScrollY.current = currentScrollY;

      // Calculate current position relative to trigger zone
      const rect = imageRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const triggerStartY = viewportHeight * 0.2; // 20% from top
      const animationEndYPos = viewportHeight * 0.01; // 1% from top

      // Use the bottom of the image instead of the top
      const currentY = rect.bottom;
      const animationRange = triggerStartY - animationEndYPos;
      const progressInRange = (triggerStartY - currentY) / animationRange;

      // Clamp progress between 0 and 1
      const clampedProgress = Math.max(0, Math.min(1, progressInRange));

      setImageAnimationProgress(clampedProgress);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Calculate interpolated values based on iOS keyframes (for scroll-tethered animation)
  const interpolatedRotation = interpolateFromKeyframes(imageAnimationProgress, [
    -0.0,
    -8.54,
    -25.35,
    -59.88,
    -109.1,
    -120.0,
  ]);
  const interpolatedScale = interpolateFromKeyframes(imageAnimationProgress, [1.0, 0.7, 0.52, 0.44, 0.2, 0.0]);

  const handleFooterClick = (target: HTMLElement) => target.tagName !== 'A' && push(sourceUrl ?? '#');

  return (
    <StyledWrap bg={bgColor} isReverse={false}>
      <Grain />
      <div style={{ position: 'relative', zIndex: 10 }}>
        <Actions
          item={card}
          fontColor={color}
          showAddReaction
          isReactionButtonMenuShown={isQuickReactionOpen}
          setIsReactionButtonMenuShown={setIsQuickReactionOpen}
          setIsReactionsModalOpen={setIsReactionsModalOpen}
          setIsQuickReactionOpen={setIsQuickReactionOpen}
          setReactionCoordinates={setReactionCoordinates}
          onOpenReactionsModal={() => setIsReactionsModalOpen(true)}
        />
      </div>
      <StyledDivider />
      <StyledLink href={sourceUrl ?? '#'} onClick={handleVibrate} target="_blank" rel="noopener noreferrer">
        <StyledContent isReverse={isVideo}>
          <Content
            dataTestId={dataTestId ?? TEST_CARD_FULL_BLEED}
            card={card}
            hugeTitle={isPlainCard}
            bigTitle
            hasPaddings
          />
          {showImage && (
            <div
              ref={imageRef}
              style={{
                borderRadius: '8px',
                overflow: 'hidden',
                position: 'relative',
                width: '100%',
                willChange: 'transform',
              }}
            >
              <StyledImage
                data-testid={getCardImageId(dataTestId ?? TEST_CARD_FULL_BLEED)}
                src={imageUrl}
                isAnimating={isAnimating}
                style={{
                  transformOrigin: 'center center',
                  transition: 'none',
                  transform: isAnimating ? 'none' : `rotate(${interpolatedRotation}deg) scale(${interpolatedScale})`,
                }}
              />
            </div>
          )}
        </StyledContent>
      </StyledLink>
      <StyledFooter
        onClick={({ target }) => handleFooterClick(target as HTMLElement)}
        hasImage={showImage}
        color={color}
      >
        <Source dataTestId={getCardSourceLabelId(dataTestId ?? TEST_CARD_FULL_BLEED)} card={card} />
        <CardFooterMenu card={card} color={color} />
      </StyledFooter>
      {highlight && <UIHighlight isStandAlone={isShort} highlight={introductionText ?? ''} curator={curator} isDark />}
      {questionId && questionTitle && (
        <QuestionAttribute
          isDark
          isFullBleed
          questionId={questionId}
          text={`Response to ${questionGuestList?.title ?? 'Question of the Day'}`}
          title={questionTitle}
          imageUrl={imageGuestUrl}
        />
      )}
      {responseToSpark && <SparkAttribute spark={responseToSpark} isFullBleed isDark />}
      {isReactionsModalOpen && <ReactionsModal id={card.id} handleClose={() => setIsReactionsModalOpen(false)} />}
    </StyledWrap>
  );
};

export default FullBleed;
